import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import { useMutation } from 'react-apollo';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import { fade, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import Spin from 'components/common/Spin';
import usePromise from 'hooks/usePromise';
import { toUserTimezone } from 'utils';
import { ADD_MESSAGE, GET_MESSAGES_SUBSCRIPTION } from 'graphql/conference';
import { useSubscription } from '@apollo/react-hooks';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
  },
  avatar: {
    marginRight: theme.spacing(2),
  },
  list: {
    overflow: 'auto',
    flex: 1,
    backgroundColor: theme.palette.background.blueGrayDark,
    padding: theme.spacing(2),
  },
  message: {
    marginBottom: theme.spacing(3),
  },
  messageLeft: {},
  messageRight: {},
  bubble: {
    borderRadius: 20,
    padding: theme.spacing(2),
  },
  bubbleRight: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderBottomRightRadius: 0,
  },
  bubbleLeft: {
    backgroundColor: theme.palette.background.paper,
    borderBottomLeftRadius: 0,
    border: '1px solid #EAE6E7',
    color: theme.palette.text.secondary,
  },
  author: {
    fontWeight: 500,
  },
  ts: {
    textAlign: 'right',
  },
  input: {
    paddingLeft: 8,
    paddingTop: 10,
    paddingBottom: 10,
    minHeight: 60,
  },
  fab: {
    backgroundColor: theme.palette.background.paper,
    border: '1px solid',
    borderColor: fade(theme.palette.text.secondary, 0.2),
    boxShadow: 'none',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(2),
  },
}));

const Chat = ({ onClose, roomId }) => {
  const classes = useStyles();
  const [text, setText] = useState('');
  const listRef = useRef();
  const [addMessage] = useMutation(ADD_MESSAGE);
  const user = useSelector((s) => s.user.user);
  const { data } = useSubscription(GET_MESSAGES_SUBSCRIPTION, {
    variables: { roomId },
  });

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [data]);

  const onSave = async () => {
    await addMessage({
      variables: {
        data: {
          room_id: roomId,
          user_id: user.id,
          message: text,
        },
      },
    });
    setText('');
  };

  const [handleSave, saving] = usePromise(onSave);

  return (
    <div className={classes.root}>
      <Paper className={classes.header}>
        <Box display="flex" alignItems="center" flex={1}>
          <Avatar src={user.user_pic} className={classes.avatar} />{' '}
          <Typography variant="h5">Chat</Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Paper>
      <div className={classes.list} ref={listRef}>
        {data?.chat.map((i) => (
          <div
            key={i.id}
            className={cx(classes.message, {
              [classes.messageLeft]: i.user.user_id !== user.id,
              [classes.messageRight]: i.user.user_id === user.id,
            })}
          >
            <Box display="flex" justifyContent="space-between" marginBottom={1}>
              <Typography color="textSecondary" variant="caption">
                {i.user.user_first_name}
              </Typography>
              <Typography color="textSecondary" variant="caption">
                {toUserTimezone(i.created_at, user.timezone).format('h:mm A')}
              </Typography>
            </Box>
            <Box
              className={cx(classes.bubble, {
                [classes.bubbleLeft]: i.user.user_id !== user.id,
                [classes.bubbleRight]: i.user.user_id === user.id,
              })}
            >
              <Typography color="inherit">{i.message}</Typography>
            </Box>
          </div>
        ))}
      </div>
      <Paper>
        <Input
          multiline
          fullWidth
          disableUnderline
          autoFocus
          className={classes.input}
          margin="none"
          placeholder="Type your message here…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rowsMax={4}
          size="large"
          endAdornment={
            <InputAdornment position="end" onClick={handleSave}>
              <IconButton color="primary" disabled={!text}>
                {saving ? <Spin spinning /> : <SendIcon />}
              </IconButton>
            </InputAdornment>
          }
        />
      </Paper>
    </div>
  );
};

export default Chat;
